/**
 * Location Data Transformation Service
 *
 * Transforms API location data into the application's Location interface.
 * Handles data normalization, type conversion, and address cleaning.
 *
 * @module location-transformer
 */

import type { Location } from '@/types/location';
import { cleanAddressPrefix } from '../utils/address-cleaner';

/**
 * API location response format (from S3 JSON endpoint)
 */
export interface ApiLocation {
  id: number;
  title?: string;
  title_with_city?: string;
  location_type: 'Tour' | 'Market' | 'Nashville';
  location_address?: {
    address?: string;
    lat?: string;
    lng?: string;
  };
  stops?: any[];
  product_ids?: string[];
  products?: { product_id: string; preorder_available: boolean; walk_up_available: boolean }[];
}

/**
 * Transform API location format to application Location interface
 *
 * @param apiLocation - Raw location data from API
 * @returns Normalized Location object
 *
 * @remarks
 * Handles multiple API field variants (title vs title_with_city).
 * Cleans redundant address prefixes using cleanAddressPrefix utility.
 * Defaults to safe values for missing data (0,0 coordinates, empty arrays).
 */
export function transformApiLocation(apiLocation: ApiLocation): Location {
  const name = apiLocation.title || apiLocation.title_with_city || '';
  const rawAddress = apiLocation.location_address?.address || '';

  return {
    id: apiLocation.id,
    name,
    type: apiLocation.location_type,
    lat: parseFloat(apiLocation.location_address?.lat || '0'),
    lng: parseFloat(apiLocation.location_address?.lng || '0'),
    address: cleanAddressPrefix(rawAddress, name),
    stops: apiLocation.stops || [],
    product_ids: apiLocation.product_ids || [],
    products: apiLocation.products || [],
  };
}

/**
 * API response format options
 */
export type ApiLocationsResponse =
  | ApiLocation[] // Flat array (legacy format)
  | { tours?: ApiLocation[]; markets?: ApiLocation[] }; // Object format (current)

/**
 * Partition API response into tours and markets arrays
 *
 * @param apiResponse - Raw API response (array or object format)
 * @returns Object with separated tours and markets arrays
 *
 * @remarks
 * Supports two API response formats for backward compatibility:
 * 1. Flat array: Partitions by location_type field
 * 2. Object format: Uses pre-partitioned tours/markets keys
 *
 * All locations are transformed using transformApiLocation.
 */
export function partitionLocations(apiResponse: ApiLocationsResponse): {
  tours: Location[];
  markets: Location[];
} {
  // Handle flat array format - partition by type
  // Nashville locations are included with tours (same structure, different styling)
  if (Array.isArray(apiResponse)) {
    const tours = apiResponse
      .filter((loc) => loc.location_type === 'Tour' || loc.location_type === 'Nashville')
      .map(transformApiLocation);

    const markets = apiResponse
      .filter((loc) => loc.location_type === 'Market')
      .map(transformApiLocation);

    return { tours, markets };
  }

  // Handle object format - use pre-partitioned keys
  return {
    tours: (apiResponse.tours || []).map(transformApiLocation),
    markets: (apiResponse.markets || []).map(transformApiLocation),
  };
}
